/**
 * ReactFlowCanvas - React component with proper ReactFlow integration
 */
import React, { useCallback, useRef, useEffect, useState } from 'react';
import '@modules/reactFlow/components/shared/NodeInteractionStyles.css';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  OnConnect,
  OnNodeDrag,
  NodeTypes,
  EdgeTypes,
  Connection,
  ReactFlowProvider,
  ReactFlowInstance,
  Viewport,
  OnNodesDelete,
  OnEdgesDelete,
  SelectionMode,
  PanOnScrollMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme } from '@mui/material';

import type {
  CanvasAnalyticsOverlay,
  Screen,
  EntityMove,
  NodeData,
  BlockVariableInfo,
  ReactFlowCanvasHandle as IReactFlowCanvasHandle
} from '@modules/reactFlow/types/flow-canvas-types';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';
import FlowBlockNode from '@modules/reactFlow/components/block/FlowBlockNode';
import FlowOnboardingScreenNodeV2 from '@modules/reactFlow/components/onboarding/FlowOnboardingScreenNodeV2';
import { CustomTransitionEdge } from '@modules/reactFlow/components/shared/CustomTransitionEdge';
import { validateConnection } from '@modules/reactFlow/utils/validators/ConnectionValidator';
import { ConnectionErrorToast } from '@modules/reactFlow/components/shared/ConnectionErrorToast';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';
import type { FlowCoordinator } from '@modules/reactFlow/services/shared/FlowCoordinator';
import { isKeyboardCapturedByOverlay } from '@modules/reactFlow/utils/keyboardGuard';
import { withEdgeAnalyticsData, withNodeAnalyticsData } from '@modules/reactFlow/components/shared/analyticsOverlayAttach';
import { useUpdateNodeInternals } from '@xyflow/react';
import {
  ZOOM_MIN,
  ZOOM_MAX,
  DEFAULT_VIEWPORT_ZOOM,
  GRID_STEP_SIZE as DEFAULT_GRID_STEP_SIZE,
  CONNECTION_RADIUS,
  PAN_ON_DRAG,
  PAN_ON_SCROLL,
  PAN_ON_SCROLL_MODE,
  PAN_ON_SCROLL_SPEED,
  ZOOM_ON_PINCH,
  ZOOM_ON_SCROLL,
  ZOOM_ON_DOUBLE_CLICK,
  MULTI_SELECTION_KEYS,
  DELETE_KEY_CODES,
  SELECTION_ON_DRAG,
  SELECTION_KEY_CODE,
  ATTRIBUTION_POSITION,
  FOCUS_ANIMATION_DURATION,
} from '@modules/reactFlow/config/canvas-constraints';
import { CANVAS_NODE_DIMENSIONS } from '@modules/reactFlow/constants/screenPreviewConfig';

// Custom node types for ReactFlow
const nodeTypes: NodeTypes = {
  'strategy-block': FlowBlockNode,
  'onboarding-screen-v2': FlowOnboardingScreenNodeV2,
};

// Custom edge types - Sprint-4: Added CustomTransitionEdge with click handler (FR-010)
const edgeTypes: EdgeTypes = {
  'transition': CustomTransitionEdge,
};

const uniqueIds = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

const mergeNodeData = (current: Node | undefined, incoming: Node): Node => ({
  ...current,
  ...incoming,
  selected: incoming.selected ?? current?.selected,
  data: {
    ...(current?.data ?? {}),
    ...incoming.data,
  },
});

const dedupeNodesById = (nodeList: Node[]): Node[] => {
  const nodesById = new Map<string, Node>();
  nodeList.forEach(node => {
    nodesById.set(node.id, mergeNodeData(nodesById.get(node.id), node));
  });
  return Array.from(nodesById.values());
};

const dedupeEdgesById = (edgeList: Edge[]): Edge[] => {
  const edgesById = new Map<string, Edge>();
  edgeList.forEach(edge => {
    edgesById.set(edge.id, {
      ...(edgesById.get(edge.id) ?? {}),
      ...edge,
      data: {
        ...(edgesById.get(edge.id)?.data ?? {}),
        ...edge.data,
      },
    });
  });
  return Array.from(edgesById.values());
};

// ❌ REMOVED: Old ReactFlowCanvasHandle interface - moved to flow-canvas-types.ts with imperative methods

interface ReactFlowCanvasProps {
  coordinator: FlowCoordinator<any, any>;
  selectedScreenIds: string[];
  launchScreenId?: string;
  onScreensSelected: (screenIds: string[], additive: boolean) => void;
  onScreensMoved: (screens: Array<{ screenId: string; newPosition: { x: number; y: number } }>) => void;
  onConnectionCompleted: (payload: { sourceEntityId: string; targetEntityId: string; sourceActionKey: string }) => void;
  onScreenDropped?: (payload: { templateId: string; position: { x: number; y: number }; snapToGrid: boolean }) => void;
  onScreensDeleted?: (screenIds: string[]) => void;
  onEdgesDeleted?: (edgeIds: string[]) => void;
  onEdgeClicked?: (edgeId: string, edgeData: any) => void; // Sprint-4: FR-010 - Edge click handler
  onAnalyticsEdgeClick?: (edgeId: string, edgeMetric: NonNullable<CanvasAnalyticsOverlay['edgesByTransitionId'][string]>) => void;
  onNodeDoubleClick?: (screenId: string) => void; // Sprint-6: FR-021 - Node double-click handler
  onNodeAction?: (entityId: string, actionKey: string, entityType?: 'block' | 'screen') => void; // Node toolbar action callback
  variablesById?: Record<string, BlockVariableInfo>; // strategy variable defs (id→info) threaded to block nodes for the assigned-variable chips
  currentAnalysisId?: string; // Spec D4 (U11): strategy insights pin threaded to block nodes for the "cites old analysis" chip
  blockSubtypesBySlug?: Record<string, { name: string; enabled: boolean }>;
  onReady?: (handle: IReactFlowCanvasHandle) => void;
  nodeViewFactory?: (screen: Screen) => any;
  analyticsOverlay?: CanvasAnalyticsOverlay;
  gridStepSize?: number;
  className?: string;
}

export const ReactFlowCanvas: React.FC<ReactFlowCanvasProps> = ({
  coordinator,
  selectedScreenIds,
  launchScreenId,
  onScreensSelected,
  onScreensMoved,
  onConnectionCompleted,
  onScreenDropped,
  onScreensDeleted,
  onEdgesDeleted,
  onEdgeClicked,
  onAnalyticsEdgeClick,
  onNodeDoubleClick,
  onNodeAction,
  variablesById,
  currentAnalysisId,
  blockSubtypesBySlug,
  onReady,
  nodeViewFactory,
  analyticsOverlay,
  gridStepSize = DEFAULT_GRID_STEP_SIZE,
  className = '',
}) => {
  // CRITICAL: This ref must remain a DOM element ref for drag & drop functionality
  // DO NOT override with useImperativeHandle - it breaks getBoundingClientRect()
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const updateNodeInternals = useUpdateNodeInternals();
  const theme = useTheme();

  // ❌ REMOVED: Local state from coordinator (screenIds, positions, transitions)
  // ❌ REMOVED: Subscription to coordinator events
  // ❌ REMOVED: reactFlowNodes and reactFlowEdges useMemo
  // Now all updates are imperative via canvasHandle

  // Initialize nodes and edges state (empty initially, populated by Initial Load)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // DEBUG: Track when nodes/edges change to identify re-render sources
  const nodesCountRef = useRef(nodes.length);
  const edgesCountRef = useRef(edges.length);

  useEffect(() => {
    if (nodes.length !== nodesCountRef.current) {
      console.log('[Canvas] 🔄 Nodes state changed:', {
        oldCount: nodesCountRef.current,
        newCount: nodes.length,
        nodeIds: nodes.map(n => n.id.slice(0, 8))
      });
      nodesCountRef.current = nodes.length;
    }
  }, [nodes]);

  useEffect(() => {
    if (edges.length !== edgesCountRef.current) {
      console.log('[Canvas] 🔄 Edges state changed:', {
        oldCount: edgesCountRef.current,
        newCount: edges.length,
        edgeIds: edges.map(e => e.id.slice(0, 16))
      });
      edgesCountRef.current = edges.length;
    }
  }, [edges]);

  useEffect(() => {
    setNodes(currentNodes => dedupeNodesById(currentNodes.map(node => {
      const decorated = withNodeAnalyticsData(node, analyticsOverlay, { onNodeAction, variablesById, currentAnalysisId });
      return { ...decorated, data: { ...decorated.data, blockSubtypesBySlug } };
    })));

    setEdges(currentEdges => dedupeEdgesById(currentEdges.map(edge => (
      withEdgeAnalyticsData(edge, analyticsOverlay, { onAnalyticsEdgeClick, onEdgeClick: onEdgeClicked })
    ))));
  }, [analyticsOverlay, blockSubtypesBySlug, onAnalyticsEdgeClick, onEdgeClicked, onNodeAction, variablesById, currentAnalysisId, setEdges, setNodes]);

  // Batch movement tracking for handling multiple nodes moved together
  const selectedNodesAtDragStartRef = useRef<string[]>([]);
  const lastDragPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Keyboard shortcuts handler - Ctrl+A to select all screens
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't intercept shortcuts when a dialog or input field is active
      if (isKeyboardCapturedByOverlay(event)) return;

      // Ctrl+A or Cmd+A - select all screens
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        event.stopPropagation();

        // Update ReactFlow internal state - select all nodes
        setNodes(currentNodes =>
          currentNodes.map(node => ({ ...node, selected: true }))
        );

        // Notify parent component
        const allScreenIds = uniqueIds(nodes.map(n => n.id));
        onScreensSelected(allScreenIds, false);

        console.log('[ReactFlowCanvas] Ctrl+A: Selected all screens:', allScreenIds.length);
      }
    };

    // Use capture phase to intercept before ReactFlow handles the event
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [nodes, setNodes, onScreensSelected]);

  // ❌ REMOVED: useEffect for updating nodes/edges from reactFlowNodes/reactFlowEdges
  // Now updates happen imperatively via canvasHandle methods

  // ❌ REMOVED: useEffect for Initial Load
  // Initial Load now happens IMPERATIVELY when Coordinator.setCanvasHandle() is called
  // This follows Phase 3.2-3.3 imperative architecture pattern:
  // Canvas is a "dumb" component that only exposes imperative methods
  // Coordinator orchestrates all data loading and Canvas updates

  // Validate connections using ReactFlow's isValidConnection prop
  const isValidConnection = useCallback(
    (connection: Edge | Connection) => {
      if (!connection.source || !connection.target) return false;

      // Get transitions from coordinator
      const allTransitions = coordinator.getAllTransitions();

      const validationResult = validateConnection(connection, allTransitions, launchScreenId);
      if (!validationResult.valid) {
        // Show error toast when connection is invalid
        setConnectionError(validationResult.errorMessage || 'Invalid connection');
        console.warn('[ReactFlowCanvas] Connection rejected:', validationResult.errorMessage);
        return false;
      }

      return true;
    },
    [coordinator, launchScreenId]
  );

  // Handle new connections (only called for valid connections)
  const onConnect: OnConnect = useCallback(
    (params: Connection) => {
      if (!params.source || !params.target || !params.sourceHandle) return;

      // IMPORTANT: Do NOT add edge to ReactFlow state here
      // The CreateEdgeCommand will handle state updates via onConnectionCompleted callback
      // This prevents duplicate edges and ensures undo/redo works correctly

      console.log('[ReactFlowCanvas] 🔌 Connection detected:', {
        source: params.source,
        target: params.target,
        handleId: params.sourceHandle
      });

      // Parse handle ID to extract sourceActionKey
      // Handle ID format: "handle:{screenId}:action:{actionKey}"
      // Generated by TransitionIdGenerator.generateHandleId()
      let sourceActionKey = 'footer.button1'; // Default fallback

      try {
        // FIXED: Use synchronous parsing (no async import)
        const parsed = TransitionIdGenerator.parseHandleId(params.sourceHandle!);
        sourceActionKey = parsed.actionKey;

        console.log('[ReactFlowCanvas] ✅ Parsed handle ID:', {
          handleId: params.sourceHandle,
          entityId: parsed.entityId,
          actionKey: parsed.actionKey
        });
      } catch (error) {
        console.warn('[ReactFlowCanvas] ⚠️ Failed to parse handle ID, using default:', {
          handleId: params.sourceHandle,
          error: error instanceof Error ? error.message : error
        });
      }

      // Emit connection event - CreateEdgeCommand will handle the rest
      onConnectionCompleted({
        sourceEntityId: params.source!,
        targetEntityId: params.target!,
        sourceActionKey,
      });
    },
    [onConnectionCompleted]
  );

  // Handle node drag start - capture ALL selected nodes at drag start
  const onNodeDragStart: OnNodeDrag = useCallback((event, node, nodes) => {
    setIsDragging(true);

    // Capture all selected nodes at drag start
    // If no nodes are selected, the dragged node itself is being moved
    const selectedNodes = nodes.filter(n => n.selected);
    const nodesToMove = selectedNodes.length > 0 ? selectedNodes : [node];

    selectedNodesAtDragStartRef.current = uniqueIds(nodesToMove.map(n => n.id));

    // Store initial positions for ALL nodes being moved
    nodesToMove.forEach(n => {
      lastDragPositionsRef.current.set(n.id, n.position);
    });

    console.log(`[ReactFlowCanvas] 🎬 Drag started - nodes to move:`, {
      count: nodesToMove.length,
      ids: nodesToMove.map(n => n.id.slice(0, 8)),
      isMultiSelect: selectedNodes.length > 0
    });
  }, []);

  // Handle node drag end - process ALL selected nodes that were dragged
  const onNodeDragStop: OnNodeDrag = useCallback(
    (event, node, nodes) => {
      setIsDragging(false);

      // Get ALL nodes that were being dragged (from drag start)
      const draggedNodeIds = selectedNodesAtDragStartRef.current;

      console.log(`[ReactFlowCanvas] 🎯 onNodeDragStop called:`, {
        triggerNodeId: node.id.slice(0, 8),
        draggedNodes: draggedNodeIds.length,
        draggedIds: draggedNodeIds.map(id => id.slice(0, 8))
      });

      // Collect moves for ALL dragged nodes
      const moves = draggedNodeIds.map(nodeId => {
        const currentNode = nodes.find(n => n.id === nodeId);
        if (!currentNode) return null;

        const snappedPosition = {
          x: Math.round(currentNode.position.x / gridStepSize) * gridStepSize,
          y: Math.round(currentNode.position.y / gridStepSize) * gridStepSize,
        };

        return {
          screenId: nodeId,
          newPosition: snappedPosition
        };
      }).filter((m): m is { screenId: string; newPosition: { x: number; y: number } } => m !== null);

      if (moves.length > 0) {
        console.log(`[ReactFlowCanvas] 📦 Processing ${moves.length} move(s):`, moves.map(m => ({
          id: m.screenId.slice(0, 8),
          pos: m.newPosition
        })));
        onScreensMoved(moves);
      }

      // Clear tracking
      selectedNodesAtDragStartRef.current = [];
      lastDragPositionsRef.current.clear();
    },
    [onScreensMoved, gridStepSize]
  );

  // Handle selection changes
  const onSelectionChange = useCallback(
    ({ nodes: selectedNodes }: { nodes: Node[]; edges: Edge[] }) => {
      const selectedIds = uniqueIds(selectedNodes.map((node) => node.id));
      onScreensSelected(selectedIds, false);
    },
    [onScreensSelected]
  );

  // Handle node deletion
  const onNodesDelete: OnNodesDelete = useCallback(
    (deletedNodes) => {
      const deletedIds = uniqueIds(deletedNodes.map((node) => node.id));
      console.log('Nodes deleted:', deletedIds);
      if (onScreensDeleted) {
        onScreensDeleted(deletedIds);
      }
    },
    [onScreensDeleted]
  );

  // Handle edge deletion
  const onEdgesDelete: OnEdgesDelete = useCallback(
    (deletedEdges) => {
      const deletedIds = uniqueIds(deletedEdges.map(e => e.id));
      console.log('Edges deleted:', deletedIds);
      if (onEdgesDeleted) {
        onEdgesDeleted(deletedIds);
      }
    },
    [onEdgesDeleted]
  );

  // Handle node double-click - Sprint-6: FR-021
  const handleNodeDoubleClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      console.log('[ReactFlowCanvas] Node double-clicked:', node.id);
      if (onNodeDoubleClick) {
        onNodeDoubleClick(node.id);
      }
    },
    [onNodeDoubleClick]
  );

  // Handle drop events for new screens
  // Architecture: Component detects drop → calculates coordinates → notifies Coordinator via callback
  // Coordinator creates new screen data → updates state → triggers React Flow re-render
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!reactFlowWrapper.current || !reactFlowInstance) {
        return;
      }

      // Validate DOM ref has required method
      if (typeof reactFlowWrapper.current.getBoundingClientRect !== 'function') {
        console.error('ReactFlowCanvas: DOM ref is invalid, cannot calculate drop coordinates');
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Snap to grid
      const snappedPosition = {
        x: Math.round(position.x / gridStepSize) * gridStepSize,
        y: Math.round(position.y / gridStepSize) * gridStepSize,
      };

      try {
        const rawData = event.dataTransfer.getData('application/json');
        const dragData = JSON.parse(rawData);

        if (onScreenDropped && dragData.templateId) {
          onScreenDropped({
            templateId: dragData.templateId,
            position: snappedPosition,
            snapToGrid: true,
          });
        }
      } catch (error) {
        console.error('ReactFlowCanvas: Failed to parse drop data', error);
      }
    },
    [reactFlowInstance, gridStepSize, onScreenDropped]
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();

    // CRITICAL: Chrome requires dropEffect to match effectAllowed
    // TemplatePanel sets effectAllowed='copy', so we must use 'copy' here too
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  // Focus on specific screen (uses ReactFlow internal nodes, works for all canvas types)
  // Centers viewport on the node's center point (not top-left corner)
  const focusOnScreen = useCallback(
    (screenId: string, zoomLevel: number = 1.5) => {
      if (!reactFlowInstance) return;

      const node = reactFlowInstance.getNode(screenId);
      if (!node) {
        console.warn('[ReactFlowCanvas] focusOnScreen: node not found, falling back to fitView:', screenId);
        reactFlowInstance.fitView({ duration: 800, padding: 0.2 });
        return;
      }

      // Calculate node center (position is top-left corner)
      const nodeWidth = node.measured?.width ?? CANVAS_NODE_DIMENSIONS.WIDTH;
      const nodeHeight = node.measured?.height ?? CANVAS_NODE_DIMENSIONS.HEIGHT;
      const centerX = (node.position.x ?? 0) + nodeWidth / 2;
      const centerY = (node.position.y ?? 0) + nodeHeight / 2;

      reactFlowInstance.setCenter(centerX, centerY, {
        zoom: zoomLevel,
        duration: FOCUS_ANIMATION_DURATION,
      });
    },
    [reactFlowInstance]
  );

  // Fit view to show all nodes
  const fitView = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ duration: 800, padding: 0.2 });
    }
  }, [reactFlowInstance]);

  // Zoom controls
  const zoomIn = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomIn({ duration: 200 });
    }
  }, [reactFlowInstance]);

  const zoomOut = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.zoomOut({ duration: 200 });
    }
  }, [reactFlowInstance]);

  // Grid-snapped flow position at the current viewport center (click-to-add).
  const getViewportCenterPosition = useCallback((): { x: number; y: number } | null => {
    if (!reactFlowInstance || !reactFlowWrapper.current) return null;
    if (typeof reactFlowWrapper.current.getBoundingClientRect !== 'function') return null;
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const center = reactFlowInstance.screenToFlowPosition({
      x: bounds.left + bounds.width / 2,
      y: bounds.top + bounds.height / 2,
    });
    return {
      x: Math.round(center.x / gridStepSize) * gridStepSize,
      y: Math.round(center.y / gridStepSize) * gridStepSize,
    };
  }, [reactFlowInstance, gridStepSize]);

  // ═══════════════════════════════════════════════════════════
  // IMPERATIVE API METHODS (Hybrid Architecture)
  // ═══════════════════════════════════════════════════════════

  const addNode = useCallback((nodeData: NodeData) => {
    console.log('[Canvas] 🎨 addNode called:', {
      entityId: nodeData.entityId.slice(0, 8),
      entityType: nodeData.entityType,
      nodeType: nodeData.nodeType,
      position: nodeData.position,
      handlesCount: nodeData.handles?.length || 0,
      isLaunchScreen: nodeData.isLaunchScreen,
      selected: nodeData.selected
    });

    const newNode: Node = {
      id: nodeData.entityId,
      type: nodeData.nodeType, // Use nodeType from data (not hardcoded)
      position: nodeData.position, // Use position from nodeData
      data: {
        ...nodeData,
        analyticsOverlay: analyticsOverlay?.nodesByScreenId[nodeData.entityId],
        onNodeAction, // Add callback from props
        variablesById, // Strategy variable defs for block-node chips
        currentAnalysisId, // Spec D4 (U11): pin for the cites-old-analysis chip
        blockSubtypesBySlug,
      } as any
    };

    setNodes(currentNodes => {
      console.log('[Canvas]   → setNodes: adding to existing', currentNodes.length, 'nodes');
      return dedupeNodesById([...currentNodes, newNode]);
    });
    console.log('[Canvas]   ✓ Node added');
  }, [setNodes, onNodeAction, analyticsOverlay, blockSubtypesBySlug, currentAnalysisId, variablesById]);

  const removeNodes = useCallback((entityIds: string[]) => {
    console.log('[Canvas] removeNodes:', entityIds);

    const idsToRemove = new Set(uniqueIds(entityIds));
    setNodes(currentNodes => currentNodes.filter(node => !idsToRemove.has(node.id)));

    console.log('[Canvas]   ✓ Nodes removed');
  }, [setNodes]);

  // Exclusively select the given nodes (deselects the rest). Marking `selected`
  // in the store both renders the selection and fires onSelectionChange.
  const selectNodes = useCallback((entityIds: string[]) => {
    const ids = new Set(uniqueIds(entityIds));
    setNodes(currentNodes => currentNodes.map(node => ({ ...node, selected: ids.has(node.id) })));
  }, [setNodes]);

  const updateNodeData = useCallback((
    entityId: string,
    updates: Partial<NodeData>
  ) => {
    console.log('[Canvas] 🔄 updateNodeData called:', {
      entityId: entityId.slice(0, 8),
      updates: {
        handles: updates.handles?.length || 'unchanged',
        isLaunchScreen: updates.isLaunchScreen !== undefined ? updates.isLaunchScreen : 'unchanged',
        selected: updates.selected !== undefined ? updates.selected : 'unchanged',
      }
    });

    setNodes(currentNodes => {
      console.log('[Canvas]   → setNodes: updating node in', currentNodes.length, 'nodes');
      return dedupeNodesById(currentNodes.map(node => {
        if (node.id === entityId) {
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              ...updates,
              onNodeAction, // Preserve callback on updates
            }
          };
          console.log('[Canvas]   → Creating new data object for', entityId.slice(0, 8));
          return updatedNode;
        }
        return node;
      }));
    });

    // Update ReactFlow internals if handles changed or isLaunchScreen toggled
    // (ConnectionInputHandle conditionally renders <Handle>, so ReactFlow needs
    // to recalculate its internal handle registry when it appears/disappears)
    if (updates.handles) {
      updateNodeInternals(entityId);
      console.log('[Canvas]   → updateNodeInternals() called (handles)');
    }
    if (updates.isLaunchScreen !== undefined) {
      // Defer: ConnectionInputHandle mounts/unmounts <Handle> conditionally,
      // so we must wait for React to commit the DOM change before ReactFlow
      // can discover the new handle element
      requestAnimationFrame(() => {
        updateNodeInternals(entityId);
        console.log('[Canvas]   → updateNodeInternals() called (isLaunchScreen, deferred)');
      });
    }

    console.log('[Canvas]   ✓ Node data updated');
  }, [setNodes, updateNodeInternals, onNodeAction]);

  const updateNodePositions = useCallback((moves: EntityMove[]) => {
    console.log('[Canvas] updateNodePositions:', moves.length);

    const movesById = new Map(
      moves
        .filter(move => move.entityId)
        .map(move => [move.entityId, move])
    );

    setNodes(currentNodes =>
      dedupeNodesById(currentNodes.map(node => {
        const move = movesById.get(node.id);
        if (move && move.newPosition.x !== undefined && move.newPosition.y !== undefined) {
          return {
            ...node,
            position: { x: move.newPosition.x, y: move.newPosition.y }
          };
        }
        return node;
      }))
    );

    console.log('[Canvas]   ✓ Positions updated');
  }, [setNodes]);

  const syncEdges = useCallback((transitions: EntityTransition[]) => {
    console.log('[Canvas] syncEdges:', transitions.length);

    const newEdges: Edge[] = transitions.map(t => ({
      id: t.id,
      source: t.sourceEntityId,
      target: t.targetEntityId,
      sourceHandle: TransitionIdGenerator.generateHandleId(
        t.sourceEntityId,
        t.sourceActionKey
      ),
      label: t.label,
      type: 'transition',
      animated: false,
      data: {
        sourceEntityId: t.sourceEntityId,
        targetEntityId: t.targetEntityId,
        sourceActionKey: t.sourceActionKey,
        analyticsOverlay: analyticsOverlay?.edgesByTransitionId[t.id],
        onAnalyticsEdgeClick,
        onEdgeClick: onEdgeClicked,
        rules: t.rules || [],
        // Per-edge visual transition override (Route.transition) — drives the
        // canvas override marker and the TransitionEditor initial value
        transition: t.transition,
      }
    }));

    // DEBUG: Detailed edge information with rules
    console.log('[Canvas] 🔗 Created edges:', newEdges.map(e => ({
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      rulesCount: (e.data as any)?.rules?.length || 0,
    })));

    // DEBUG: Log transitions with rules
    const transitionsWithRules = transitions.filter(t => t.rules && t.rules.length > 0);
    if (transitionsWithRules.length > 0) {
      console.log('[Canvas] 📋 Transitions with rules:', transitionsWithRules.map(t => ({
        id: t.id,
        source: t.sourceEntityId,
        target: t.targetEntityId,
        rulesCount: t.rules?.length,
        rules: t.rules,
      })));
    }

    setEdges(dedupeEdgesById(newEdges));
    console.log('[Canvas]   ✓ Edges synced');
  }, [setEdges, onEdgeClicked, onAnalyticsEdgeClick, analyticsOverlay]);

  // Point update methods for edges
  const addSingleEdge = useCallback((newEdge: Edge) => {
    console.log('[Canvas] 🔗 addSingleEdge called:', newEdge.id);

    // Ensure edge has onEdgeClick callback in data
    const edgeWithCallback = {
      ...newEdge,
      data: {
        ...newEdge.data,
        analyticsOverlay: analyticsOverlay?.edgesByTransitionId[newEdge.id],
        onAnalyticsEdgeClick,
        onEdgeClick: onEdgeClicked
      }
    };

    setEdges(prev => {
      // Check for duplicates
      if (prev.some(e => e.id === edgeWithCallback.id)) {
        console.warn('[Canvas] Edge already exists:', edgeWithCallback.id);
        return dedupeEdgesById(prev);
      }
      return dedupeEdgesById([...prev, edgeWithCallback]);
    });

    console.log('[Canvas]   ✓ Edge added');
  }, [setEdges, onEdgeClicked, onAnalyticsEdgeClick, analyticsOverlay]);

  const updateSingleEdge = useCallback((updatedEdge: Edge) => {
    console.log('[Canvas] 🔄 updateSingleEdge called:', updatedEdge.id);

    // Ensure edge has onEdgeClick callback in data
    const edgeWithCallback = {
      ...updatedEdge,
      data: {
        ...updatedEdge.data,
        analyticsOverlay: analyticsOverlay?.edgesByTransitionId[updatedEdge.id],
        onAnalyticsEdgeClick,
        onEdgeClick: onEdgeClicked
      }
    };

    setEdges(prev => dedupeEdgesById(
      prev.map(e => e.id === edgeWithCallback.id ? edgeWithCallback : e)
    ));

    console.log('[Canvas]   ✓ Edge updated');
  }, [setEdges, onEdgeClicked, onAnalyticsEdgeClick, analyticsOverlay]);

  const removeSingleEdge = useCallback((edgeId: string) => {
    console.log('[Canvas] 🗑️ removeSingleEdge called:', edgeId);

    setEdges(prev => prev.filter(e => e.id !== edgeId));

    console.log('[Canvas]   ✓ Edge removed');
  }, [setEdges]);

  const removeMultipleEdges = useCallback((edgeIds: string[]) => {
    console.log('[Canvas] 🗑️ removeMultipleEdges called:', edgeIds.length);

    const idsToRemove = new Set(uniqueIds(edgeIds));
    setEdges(prev => prev.filter(e => !idsToRemove.has(e.id)));

    console.log('[Canvas]   ✓ Edges removed');
  }, [setEdges]);

  const clearCanvas = useCallback(() => {
    console.log('[Canvas] 🧹 clearCanvas called - clearing all nodes and edges');

    setNodes([]);
    setEdges([]);

    console.log('[Canvas]   ✓ Canvas cleared completely');
  }, [setNodes, setEdges]);

  // Expose handle methods to parent via onReady callback
  useEffect(() => {
    if (reactFlowInstance && onReady) {
      const handle: IReactFlowCanvasHandle = {
        // View control methods
        fitView,
        focusOnScreen,
        zoomIn,
        zoomOut,
        getViewportCenterPosition,

        // Imperative node methods
        addNode,
        removeNodes,
        selectNodes,
        updateNodeData,
        updateNodePositions,

        // Imperative edge methods
        syncEdges,
        addEdge: addSingleEdge,
        updateEdge: updateSingleEdge,
        removeEdge: removeSingleEdge,
        removeEdges: removeMultipleEdges,

        // Canvas clearing
        clearCanvas,

        // Spec D D1b: plain-array snapshot for the read-only analytics canvas
        getGraphSnapshot: () => ({
          nodes: reactFlowInstance.getNodes() as unknown as Array<Record<string, unknown>>,
          edges: reactFlowInstance.getEdges() as unknown as Array<Record<string, unknown>>,
        }),
      };
      onReady(handle);
      console.log('[Canvas] Handle ready and exposed');
    }
  }, [
    reactFlowInstance,
    onReady,
    fitView,
    focusOnScreen,
    zoomIn,
    zoomOut,
    getViewportCenterPosition,
    addNode,
    removeNodes,
    selectNodes,
    updateNodeData,
    updateNodePositions,
    syncEdges,
    addSingleEdge,
    updateSingleEdge,
    removeSingleEdge,
    removeMultipleEdges,
    clearCanvas,
  ]);

  // Note: Removed useImperativeHandle that was overriding reactFlowWrapper ref
  // The reactFlowWrapper should stay as a DOM element ref for getBoundingClientRect

  return (
    <div className={`reactflow-wrapper ${isDragging ? 'dragging' : ''} ${className}`} ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        connectionRadius={CONNECTION_RADIUS}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        onSelectionChange={onSelectionChange}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onNodeDoubleClick={handleNodeDoubleClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onInit={setReactFlowInstance}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultViewport={{ x: 0, y: 0, zoom: DEFAULT_VIEWPORT_ZOOM }}
        attributionPosition={ATTRIBUTION_POSITION}
        snapToGrid={true}
        snapGrid={[gridStepSize, gridStepSize]}
        deleteKeyCode={DELETE_KEY_CODES[0]}
        multiSelectionKeyCode={MULTI_SELECTION_KEYS[0]}
        selectionOnDrag={SELECTION_ON_DRAG}
        selectionKeyCode={SELECTION_KEY_CODE}
        panOnDrag={PAN_ON_DRAG}
        panOnScroll={PAN_ON_SCROLL}
        panOnScrollMode={PAN_ON_SCROLL_MODE as PanOnScrollMode}
        panOnScrollSpeed={PAN_ON_SCROLL_SPEED}
        zoomOnScroll={ZOOM_ON_SCROLL}
        zoomOnPinch={ZOOM_ON_PINCH}
        zoomOnDoubleClick={ZOOM_ON_DOUBLE_CLICK}
        selectionMode={SelectionMode.Partial}
        minZoom={ZOOM_MIN}
        maxZoom={ZOOM_MAX}
        onlyRenderVisibleElements={true}
      >
        <Controls
          style={{
            background: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            boxShadow: theme.shadows[2],
            // @ts-expect-error CSS custom properties for button styling
            '--xy-controls-button-background-color': theme.palette.background.paper,
            '--xy-controls-button-background-color-hover': theme.palette.action.hover,
            '--xy-controls-button-color': theme.palette.text.primary,
            '--xy-controls-button-color-hover': theme.palette.text.primary,
            '--xy-controls-button-border-color': theme.palette.divider,
          }}
        />
        <MiniMap
          style={{
            background: theme.palette.background.paper,
            borderColor: theme.palette.divider,
          }}
          maskColor={theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'}
          nodeColor={theme.palette.primary.main}
        />
        <Background variant={BackgroundVariant.Dots} gap={gridStepSize} size={1} />
      </ReactFlow>

      <ConnectionErrorToast
        error={connectionError}
        onClose={() => setConnectionError(null)}
      />
    </div>
  );
};

// Provider wrapper for ReactFlow context
export const ReactFlowCanvasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ReactFlowProvider>{children}</ReactFlowProvider>
);

// Export ReactFlowCanvasHandle type for external use
export type { ReactFlowCanvasHandle } from '@modules/reactFlow/types/flow-canvas-types';

// Spec D D1b: the read-only analytics canvas reuses the SAME renderer maps so
// node/edge presentation stays a single source of truth.
export { nodeTypes as canvasNodeTypes, edgeTypes as canvasEdgeTypes };
